import { defineStore } from "pinia";
import apiClient from "@/core/buildRequest";
import {
  createCacheKey,
  getModuleCache,
  setModuleCache,
} from "@/core/moduleCache";

const CACHE_MODULE = "ads-performance";

export const adsPerformance = defineStore("adsPerformance", {
  state: () => ({
    title_date: "Last 30 Days",
    campaignNumber: 0,
    adsReportStatus: 1,
    impressions: 0,
    amountSpent: 0,
    selectedPlatform: "",
    selectedStatus: null,
    selectedDate: null,
    start_date: "",
    end_date: "",
    tiktok_advertiser_id: null,
    facebook_ad_account_id: null,
    snap_ad_account_id: null,
    leads: 0,
    clicks: 0,
    linksData: [
      { name: "home", link: "/" },
      { name: "ads_performance", link: "" },
    ],
    data: {},
    tableData: [],
    loadingFortable: true,
    pagination: {
      current_page: 1,
      per_page: 10,
      total: 0,
    },
  }),
  actions: {
    setPlatform(obj) {
      this.selectedPlatform = obj;
    },
    setStatus(val) {
      this.selectedStatus = val;
    },
    setSelectedDate(arr) {
      this.selectedDate = arr;
      if (arr.length) {
        const dates = arr.sort((a,b)=>new Date(a)-new Date(b));
        this.start_date = dates[0].toISOString().split('T')[0];
        this.end_date = dates[dates.length-1].toISOString().split('T')[0];
      } else {
        this.start_date = this.end_date = null;
      }
    },
    formatNumber(number) {
      if (!number && number !== 0) return "-";
      return number
        .toFixed(2)
        .replace(/\d(?=(\d{3})+\.)/g, "$&,")
        .replace(/\.00$/, "");
    },
    async getData(page = 1, perPage = 10, platform = "") {
      this.loadingFortable = true;
      // نحدث بيانات الصفحة والـ per_page جوه الستور قبل ما نطلب
      this.pagination.current_page = page;
      this.pagination.per_page = perPage;
      let filters = {
        status: this.selectedStatus,
        start_time: this.start_date,
        end_time: this.end_date,
        platform: this.selectedPlatform
          ? this.selectedPlatform.platform.toLowerCase()
          : undefined,
      };
      Object.keys(filters).forEach((key) => {
        if (!filters[key]) {
          delete filters[key];
        }
      });
      const requestData = {
        facebook_ad_account_id: this.facebook_ad_account_id,
        snap_ad_account_id: this.snap_ad_account_id,
        tiktok_advertiser_id: this.tiktok_advertiser_id,
        filters: filters,
      };
      const cacheKey = createCacheKey({ page, perPage, requestData });
      const cached = getModuleCache(CACHE_MODULE, cacheKey);
      if (cached?.data) {
        this.applyPerformanceData(cached.data);
        this.loadingFortable = false;
      } else {
        this.resetMetrics();
      }
      try {
        const performanceResponse = await apiClient.post(
          `/ad-performance/all/socials/performance?page=${page}&per_page=${perPage}`,
          requestData
        );
        const responseData = performanceResponse.data.data;
        this.applyPerformanceData(responseData);
        setModuleCache(CACHE_MODULE, cacheKey, responseData);
        this.title_date = this.getDateTitle();
      } catch (error) {
        console.error("Error:", error.response?.data || error.message);
      } finally {
        this.loadingFortable = false;
      }
    },
    applyPerformanceData(responseData) {
      if (!responseData?.pagination || !responseData?.summations) return;
      this.tableData = responseData.data || [];
      this.pagination.total = responseData.pagination.total || 0;
      this.pagination.current_page =
        responseData.pagination.current_page || this.pagination.current_page;
      this.campaignNumber = responseData.summations.campaign_sum || 0;
      this.impressions = responseData.summations.impressions || 0;
      this.amountSpent = responseData.summations.spend || 0;
      this.leads = responseData.summations.native_leads || 0;
      this.clicks = responseData.summations.clicks || 0;
      this.title_date = this.getDateTitle();
    },
    resetFilters() {
      this.selectedStatus = null;
      this.start_date = null;
      this.end_date = null;
      this.selectedPlatform = null;
    },

    resetMetrics() {
      this.clicks = 0;
      this.leads = 0;
      this.campaignNumber = 0;
      this.amountSpent = 0;
      this.impressions = 0;
      this.tableData = [];
    },
    processPlatformData(platformData, platformName) {
      platformData.performance.campaigns.forEach((item) => {
        this.tableData.push({
          platform: platformName,
          ad_account_id: item.ad_account_id,
          id: item.id,
          checked: false,
          name: item.name,
          status: item.status,
          start_time: item.start_time,
          end_time: item.end_time,
          delivery_status: item.delivery_status,
          lifetime_spend_cap_micro: item.lifetime_spend_cap_micro,
          native_leads: item?.stats?.native_leads,
          calculations: {
            costPerClick: item.calculations?.costPerClick || null,
            eCPM: item.calculations?.eCPM || null,
            eCPC: item.calculations?.eCPC || null,
          },
          stats: {
            impressions: item.stats?.impressions || 0,
            clicks: item.stats?.clicks || 0,
          },
        });
      });

      this.clicks += platformData.performance.summations.clicks;
      this.leads += platformData.performance.summations.leadsSum;
      this.campaignNumber += platformData.performance.summations.campaign_sum;
      this.amountSpent += platformData.performance.summations.spendSum;
      this.impressions += platformData.performance.summations.impressionSum;
    },
    updateChartData(snapchat, tiktok) {
      const datasets = this.data.datasets;
      [datasets[0].data, datasets[1].data] = [0, 1].map((i) => [
        i === 0 ? snapchat : tiktok.performance.summations.impressionSum,
        (i === 0 ? snapchat : tiktok).performance.summations.spendSum,
        (i === 0 ? snapchat : tiktok).performance.summations.leadsSum,
        (i === 0 ? snapchat : tiktok).performance.summations.clicksSum,
        (i === 0 ? snapchat : tiktok).performance.summations.campaign_sum,
      ]);
    },
    getDateTitle() {
      return this.start_date
        ? `From : ${this.start_date} To : ${this.end_date}`
        : "Last 30 Days";
    },
    ApplyFilter() {
      this.getData();
    },

    truncateName(name, length) {
      if (!name) return "";
      return name.length > length ? name.slice(0, length) + "..." : name;
    },
  },
});
